/**
 * ConnectionsSection component for displaying connector usage from egress telemetry.
 * Shows tiles for each connection with live request counts.
 *
 * @module components/connectors/ConnectionsSection
 */

import { useMemo } from 'react';
import { Link2, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardHeader, CardBody, Badge } from '@/components/ui';
import type { EgressDecisionEntry } from '@/types';

interface ConnectionStats {
  /** Identifier for grouping: matchedRule or connectionId */
  id: string;
  /** Display name: connectionName, matchedRule, or truncated connectionId */
  displayLabel?: string;
  allowedCount: number;
  deniedCount: number;
  totalCount: number;
  /** Last request timestamp */
  lastSeen: Date;
}

interface ConnectionsSectionProps {
  /** Allowed egress entries */
  allowedEntries: EgressDecisionEntry[];
  /** Denied egress entries */
  deniedEntries: EgressDecisionEntry[];
  /** Whether data is currently loading */
  isLoading?: boolean;
  /** Whether to render without Card wrapper (for use in panels/sections) */
  inline?: boolean;
}

/**
 * Aggregates egress entries by matchedRule or connectionId and computes stats.
 * Prefers matchedRule (from rules-based evaluation) over connectionId (legacy URL pattern matching).
 */
function aggregateConnectorActivity(
  allowedEntries: EgressDecisionEntry[],
  deniedEntries: EgressDecisionEntry[]
): ConnectionStats[] {
  const statsMap = new Map<string, ConnectionStats>();

  function processEntry(entry: EgressDecisionEntry, type: 'allowed' | 'denied') {
    // Use matchedRule as primary key, fall back to connectionId
    const key = entry.matchedRule || entry.connectionId;
    if (!key) return;

    const label = entry.connectionName || entry.matchedRule || entry.connectionId;
    const entryTime = new Date(entry.timestamp);
    const existing = statsMap.get(key);

    if (existing) {
      if (type === 'allowed') existing.allowedCount++;
      else existing.deniedCount++;
      existing.totalCount++;
      if (entryTime > existing.lastSeen) existing.lastSeen = entryTime;
      if (label && !existing.displayLabel) existing.displayLabel = label;
    } else {
      statsMap.set(key, {
        id: key,
        displayLabel: label,
        allowedCount: type === 'allowed' ? 1 : 0,
        deniedCount: type === 'denied' ? 1 : 0,
        totalCount: 1,
        lastSeen: entryTime,
      });
    }
  }

  for (const entry of allowedEntries) processEntry(entry, 'allowed');
  for (const entry of deniedEntries) processEntry(entry, 'denied');

  // Sort by total count descending
  return Array.from(statsMap.values()).sort((a, b) => b.totalCount - a.totalCount);
}

/**
 * Truncates a string for display.
 */
function truncateString(str: string, length: number = 8): string {
  if (str.length <= length) return str;
  return `${str.substring(0, length)}...`;
}

/**
 * The inner content of the connections section.
 */
function ConnectionsContent({
  connectionStats,
  isLoading,
}: {
  connectionStats: ConnectionStats[];
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading connection data...</p>;
  }

  if (connectionStats.length === 0) {
    return (
      <div className="text-center py-6">
        <Link2 className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">No connector activity detected.</p>
        <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
          Connector calls will appear here when your app uses configured connections.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {connectionStats.map((stats) => {
        const displayName = stats.displayLabel || truncateString(stats.id, 12);
        return (
          <div
            key={stats.id}
            className="relative p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            {/* Connection Name */}
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="h-4 w-4 text-primary-500 flex-shrink-0" />
              <span
                className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
                title={stats.displayLabel ? `${stats.displayLabel} (${stats.id})` : stats.id}
              >
                {displayName}
              </span>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between">
              {/* Total count - prominent */}
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalCount}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">requests</div>
              </div>

              {/* Allowed/Denied breakdown */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1 text-xs">
                  <ArrowUpRight className="h-3 w-3 text-success-500" />
                  <span className="text-success-700 dark:text-success-400 font-medium">
                    {stats.allowedCount}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">allowed</span>
                </div>
                {stats.deniedCount > 0 && (
                  <div className="flex items-center gap-1 text-xs">
                    <ArrowDownRight className="h-3 w-3 text-danger-500" />
                    <span className="text-danger-700 dark:text-danger-400 font-medium">
                      {stats.deniedCount}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">denied</span>
                  </div>
                )}
              </div>
            </div>

            {/* Last seen */}
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-400 dark:text-gray-400">
                Last: {stats.lastSeen.toLocaleTimeString()}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Displays connection usage statistics from egress telemetry.
 * Shows a tile for each connection with request counts.
 */
export function ConnectionsSection({
  allowedEntries,
  deniedEntries,
  isLoading,
  inline = false,
}: ConnectionsSectionProps) {
  const connectionStats = useMemo(
    () => aggregateConnectorActivity(allowedEntries, deniedEntries),
    [allowedEntries, deniedEntries]
  );

  // Inline mode: render without Card wrapper (for use in panels/sections)
  if (inline) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Connections</h4>
          </div>
          {connectionStats.length > 0 && (
            <Badge variant="info">{connectionStats.length} active</Badge>
          )}
        </div>
        <ConnectionsContent connectionStats={connectionStats} isLoading={isLoading} />
      </div>
    );
  }

  // Card mode: render with Card wrapper (for use in grid layouts)
  return (
    <Card className="lg:col-span-2">
      <CardHeader
        title="Connections"
        icon={<Link2 className="h-5 w-5 text-primary-500" />}
        action={
          connectionStats.length > 0 ? (
            <Badge variant="info">{connectionStats.length} active</Badge>
          ) : undefined
        }
      />
      <CardBody>
        <ConnectionsContent connectionStats={connectionStats} isLoading={isLoading} />
      </CardBody>
    </Card>
  );
}
